/**
 * CircuitDetail page — shows all circuit fields, linked contract, linked site,
 * associated installs and disconnects.
 *
 * Requirements: 34.6, 34.7, 34.8
 */

import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { circuitService } from '@/services/circuitService';

import { StatusBadge, type BadgeVariant } from '@/components/ui/StatusBadge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';

const LIFECYCLE_VARIANTS: Record<string, BadgeVariant> = {
  Ordered: 'blue',
  'In Progress': 'yellow',
  Installed: 'blue',
  Active: 'green',
  'Pending Disconnect': 'yellow',
  Disconnected: 'red',
};

export function CircuitDetail() {
  const { id } = useParams<{ id: string }>();
  const circuitId = Number(id);

  const { data: circuit, isLoading, error, refetch } = useQuery({
    queryKey: ['circuit', circuitId],
    queryFn: () => circuitService.getById(circuitId),
    enabled: !isNaN(circuitId),
  });
  if (isNaN(circuitId)) {
    return <ErrorMessage message="Invalid circuit ID." />;
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading circuit details…" />;
  }

  if (error || !circuit) {
    return (
      <ErrorMessage
        message={(error as Error)?.message ?? 'Failed to load circuit.'}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
          <Link to="/circuits" style={{ color: '#64748b', textDecoration: 'none', fontSize: '0.8rem' }}>
            ← Circuits
          </Link>
        </div>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>
          Circuit #{circuit.circuitId} — {circuit.circuitIdentifier ?? 'Unnamed'}
        </h1>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', alignItems: 'center' }}>
          <StatusBadge
            label={circuit.lifecycleStatus}
            variant={LIFECYCLE_VARIANTS[circuit.lifecycleStatus] ?? 'gray'}
          />
          {circuit.accessType && (
            <StatusBadge label={circuit.accessType} variant="gray" />
          )}
        </div>
      </div>

      {/* Circuit Information */}
      <section style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginTop: 0, marginBottom: '0.75rem' }}>Circuit Information</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem', fontSize: '0.875rem' }}>
          <div><span style={{ color: '#64748b' }}>Circuit ID:</span> {circuit.circuitId}</div>
          <div>
            <span style={{ color: '#64748b' }}>Site:</span>{' '}
            <Link to={`/sites/${circuit.siteNumber}`} style={{ color: '#c8102e', textDecoration: 'none' }}>
              {circuit.siteNumber}
            </Link>
          </div>
          <div><span style={{ color: '#64748b' }}>Circuit Identifier:</span> {circuit.circuitIdentifier ?? '—'}</div>
          <div><span style={{ color: '#64748b' }}>Account Number:</span> {circuit.accountNumber ?? '—'}</div>
          <div><span style={{ color: '#64748b' }}>Carrier/Vendor:</span> {circuit.carrierVendor ?? '—'}</div>
          <div><span style={{ color: '#64748b' }}>Access Type:</span> {circuit.accessType ?? '—'}</div>
          <div><span style={{ color: '#64748b' }}>Bandwidth:</span> {circuit.bandwidthMbps ? `${circuit.bandwidthMbps} Mbps` : '—'}</div>
          <div><span style={{ color: '#64748b' }}>Circuit Type:</span> {circuit.circuitTypeClass ?? '—'}</div>
          <div><span style={{ color: '#64748b' }}>MRC:</span> {circuit.mrc != null ? `$${Number(circuit.mrc).toLocaleString()}` : '—'}</div>
          <div><span style={{ color: '#64748b' }}>Primary/Secondary:</span> {circuit.primarySecondary ?? '—'}</div>
          {circuit.contractId && (
            <div>
              <span style={{ color: '#64748b' }}>Contract:</span>{' '}
              <Link to={`/contracts/${circuit.contractId}`} style={{ color: '#c8102e', textDecoration: 'none' }}>
                #{circuit.contractId}
              </Link>
            </div>
          )}
        </div>
        {circuit.notes && (
          <div style={{ marginTop: '0.75rem', fontSize: '0.875rem' }}>
            <span style={{ color: '#64748b' }}>Notes:</span> {circuit.notes}
          </div>
        )}
      </section>

      {/* IP Addressing */}
      <section style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginTop: 0, marginBottom: '0.75rem' }}>IP Addressing</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem', fontSize: '0.875rem' }}>
          <div><span style={{ color: '#64748b' }}>WAN IP:</span> {circuit.wanIPAddress ?? '—'}</div>
          <div><span style={{ color: '#64748b' }}>Subnet:</span> {circuit.subnet ?? '—'}</div>
          <div><span style={{ color: '#64748b' }}>Encapsulation:</span> {circuit.encapsulation ?? '—'}</div>
        </div>
      </section>
    </div>
  );
}
